import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { useParams, useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import { Card, Tabs, Tab } from '@mui/material';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import { IOrderResponse } from '../../../../interfaces/order';
import homeStyles from '../../../../constants/homeStyles';
import { setLayoutFooter, setLayoutSubheader } from '../../../../utils/layout';
import AlertDialog from '../../../../components/other/Dialog/AlertDialog';
import { TabPanel } from '../../../../components/other/Tab/TabPanel';
import { useDefineUserRole } from '../../../../hooks';
import { OrderForm } from './OrderForm';
import { useGetOrder } from './hooks/useGetOrder';
import { useCreateOrder } from './hooks/useCreateOrder';
import { useEditOrder } from './hooks/useEditOrder';
import { IAppState } from '../../../../store/rootDuck';

const getInitialValues = (order?: IOrderResponse) => {
  const values = {
    fio: order?.fio || '',
    phone: order?.phone || '' || '',
    comment: order?.comment || '',
    commentDriver: order?.comments?.driver?.text || '',
    status: order?.status || 'formed',
    startLocation: {
      lat: order?.start_location?.lat || 0,
      lng: order?.start_location?.lng || 0,
      address: order?.start_location?.address || '',
      fullAddress: '',
      province: order?.start_location?.province || '',
    },
    endLocation: {
      lat: order?.end_location?.lat || 0,
      lng: order?.end_location?.lng || 0,
      address: order?.end_location?.address || '',
      fullAddress: '',
      province: order?.end_location?.province || '',
    },
    driver: order?.driver,
    user: order?.user,
    time: order?.time || 0,
    length: order?.length || 0,
    tariff: order?.tariff,
    summ: order?.summ || 0,
    fixSumm: order?.fix_summ || 0,
    calcSumm: order?.calc_summ || 0,
  };
  return values;
};

export enum ViewMode {
  VIEW = 'view',
  EDIT = 'edit',
}

export function OrderPage() {
  const { me } = useSelector((store: IAppState) => ({
    me: store.profile.me,
  }));
  const [isAlertOpen, setAlertOpen] = useState(false);
  const { fetchOrder, loading, order } = useGetOrder();
  const { createOrderTaxi, createdOrder, loadingCreated, successCreated } = useCreateOrder();
  const { editOrderTaxi, editedOrder, loadingEdit, successEdit } = useEditOrder();
  const isEditOrder = useDefineUserRole(me, [
    'ROLE_MANAGER',
    'ROLE_ADMIN',
    'ROLE_ORDER_OPERATOR',
  ]);
  const isOper = useDefineUserRole(me, 'ROLE_ORDER_OPERATOR');
  const [valueTabs, setValueTabs] = useState(0);
  const intl = useIntl();
  const navigate = useNavigate();
  const { id, mode } = useParams();
  const { enqueueSnackbar } = useSnackbar();
  const homeClasses = homeStyles();

  useEffect(() => {
    if (id) {
      fetchOrder(+id);
    }
  }, [id]);

  useEffect(() => {
    if (successEdit) {
      enqueueSnackbar(
        ` ${intl.formatMessage({
          id: 'ORDER.SNACKBAR.EDIT',
        })}`,
        { variant: 'success' }
      );
      if (successEdit) {
        id && fetchOrder(+id);
      }
    }
  }, [successEdit, id]);

  useEffect(() => {
    if (successCreated) {
      enqueueSnackbar(
        ` ${intl.formatMessage({
          id: 'CART.CHECKOUT.SUCCESS',
        })}`,
        { variant: 'success' }
      );
      if (createdOrder) {
        // navigate(`/orders/${createdOrder.id}/edit`, { replace: true });
        navigate(-1);
      }
    }
  }, [successEdit, id, createdOrder]);

  const formik = useFormik({
    initialValues: getInitialValues(order),
    validationSchema: Yup.object().shape({
      startLocation: Yup.object().shape({
        address: Yup.string()
          .nullable()
          .required(intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED_FIELD' })),
      }),
      endLocation: Yup.object().shape({
        address: Yup.string()
          .nullable()
          .required(intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED_FIELD' })),
      }),
      user: Yup.object()
        .nullable()
        .shape({
          id: Yup.number()
            .nullable()
            .required(intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED_FIELD' })),
        })
        .required(intl.formatMessage({ id: 'AUTH.VALIDATION.REQUIRED_FIELD' })),
    }),
    onSubmit: submitValues => {
      if (id) {
        editOrderTaxi(+id, {
          status: submitValues.status,
          start: {
            lat: submitValues.startLocation.lat || 0,
            lng: submitValues.startLocation.lng || 0,
            address:
              submitValues.startLocation.fullAddress ||
              submitValues.startLocation.address ||
              '',
            province: submitValues.startLocation.province || null,
          },
          end: {
            lat: submitValues.endLocation.lat || 0,
            lng: submitValues.endLocation.lng || 0,
            address:
              submitValues.endLocation.fullAddress || submitValues.endLocation.address || '',
            province: submitValues.endLocation.province || null,
          },
          driverId: submitValues.driver?.id || null,
          // driver_id: submitValues.driver?.id || null,
          comment: submitValues.comment,
          length: submitValues.length,
          time: submitValues.time,
          tariffId: submitValues.tariff?.id,
          user_id: submitValues.user?.id,
          fixSumm: isOper || me?.is_admin ? +submitValues.fixSumm || null : undefined,
        });
      } else {
        createOrderTaxi(
          {
            fio: submitValues.fio || '',
            phone: submitValues.phone || '',
            time: submitValues.time,
            length: submitValues.length,
            start: {
              lat: submitValues.startLocation.lat || 0,
              lng: submitValues.startLocation.lng || 0,
              address:
                submitValues.startLocation.fullAddress ||
                submitValues.startLocation.address ||
                '',
              province: submitValues.startLocation.province || null,
            },
            end: {
              lat: submitValues.endLocation.lat || 0,
              lng: submitValues.endLocation.lng || 0,
              address:
                submitValues.endLocation.fullAddress || submitValues.endLocation.address || '',
              province: submitValues.endLocation.province || null,
            },
            driverId: submitValues.driver?.id,
            comment: submitValues.comment,
            tariffId: submitValues.tariff?.id,
            fixSumm: isOper || me?.is_admin ? +submitValues.fixSumm || undefined : undefined,
          },
          submitValues.user?.id
        );
      }
    },
  });

  const { handleSubmit, setValues } = formik;

  useEffect(() => {
    if (order) {
      setValues(getInitialValues(order));
    }
  }, [order, setValues]);

  const translates: { [type: string]: string } = {
    formed: intl.formatMessage({ id: 'ORDER.TYPE.CART' }),
    new: intl.formatMessage({ id: 'ORDER.TYPE.NEW' }),
    driver_found: intl.formatMessage({ id: 'ORDER.TYPE.FORMATION' }),
    driver_waiting: intl.formatMessage({ id: 'ORDER.TYPE.DRIVER_WAITING' }),
    driver_coming: intl.formatMessage({ id: 'ORDER.TYPE.DELIVERY' }),
    way: intl.formatMessage({ id: 'ORDER.TYPE.PAYED' }),
    completed: intl.formatMessage({ id: 'ORDER.TYPE.COMPLETED' }),
    canceled: intl.formatMessage({ id: 'ORDER.TYPE.CANCELED' }),
  };

  const disabled = mode === ViewMode.VIEW;

  setLayoutSubheader({
    title: id
      ? `${intl.formatMessage({
          id: mode === ViewMode.VIEW ? 'ORDER.HEADER.VIEW' : 'ORDER.HEADER.EDIT',
        })}`
      : `${intl.formatMessage({ id: 'ORDER.HEADER.CREATE' })}`,
  });
  setLayoutFooter({ show: true });

  const submitCallback = useCallback(() => {
    if (
      order &&
      (order.status === 'payed' ||
        order.status === 'completed' ||
        order.status === 'ready_to_delivery')
    ) {
      setAlertOpen(true);
    } else {
      handleSubmit();
    }
  }, [order, handleSubmit]);

  const handleTabsChange = (event: any, newValue: number) => {
    setValueTabs(newValue);
  };

  const alertText = `${intl.formatMessage({ id: 'ORDER.ALERT.EDIT.TEXT1' })} "${
    translates[order?.status || '']
  }". ${intl.formatMessage({ id: 'ORDER.ALERT.EDIT.TEXT2' })}`;

  if (loading || (!isEditOrder && mode === ViewMode.EDIT)) {
    return <></>;
  }

  return (
    <>
      <AlertDialog
        open={isAlertOpen}
        message={alertText}
        okText={intl.formatMessage({ id: 'CATEGORIES.DELETE.OK' })}
        cancelText={intl.formatMessage({ id: 'CATEGORIES.DELETE.CANCEL' })}
        handleClose={() => {
          setAlertOpen(false);
        }}
        handleAgree={() => handleSubmit()}
      />
      <Card className={homeClasses.classes.container}>
        <Tabs
          value={valueTabs}
          onChange={handleTabsChange}
          variant='scrollable'
          indicatorColor='primary'
          textColor='primary'
          aria-label='tabs'
        >
          <Tab label={intl.formatMessage({ id: 'ORDER.VIEW.TABS.FIELDS' })} />
        </Tabs>
        <div className={homeClasses.classes.table}>
          <TabPanel value={valueTabs} index={0}>
            <OrderForm
              formik={formik}
              disabled={disabled}
              order={order}
              loading={loading}
              editLoading={loadingEdit || loadingCreated}
              onSubmit={submitCallback}
              isEdit={!!id}
            />
          </TabPanel>
        </div>
      </Card>
    </>
  );
}
